import { useField } from 'formik';
import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { LockKeyholeIcon, LockKeyholeOpenIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import {
  DIMENSION_BAR_ASPECT_RATIO_BUTTON,
  DIMENSION_BAR_HEIGHT_INPUT,
  DIMENSION_BAR_WIDTH_INPUT,
} from '~/constants/testIDs';
// eslint-disable-next-line no-restricted-imports -- PLA-1724 — pre-existing debt, not introduced by the icon migration
import { DimensionBarInput } from './components/DimensionBarInput';
// eslint-disable-next-line no-restricted-imports -- PLA-1724 — pre-existing debt, not introduced by the icon migration
import { calculateAspectRatio, calculateDimensionWithAspectRatio } from './utils';

export type DimensionUnits = 'px';
export type DimensionMeasurement = 'width' | 'height';
export type DimensionBarLabelPlacement = 'inside' | 'outside';

/**
 * @description These are the minimum requirements the consuming Formik form must have as fields (and their resp. types) to use the Dimension Bar.`Size` refers to the aspect ratio and will be converted to a number (i.e. '2' becomes the number 2, and '1.78' becomes 1.78).
 */
export type DimensionBarFormData = {
  width: number;
  height: number;
  size: string | SizeValue;
};

/**
 * @description These are the minimum requirements for the  options in the size single select
 */
export enum SizeValue {
  Custom = '0',
  Original = '1',
}

export type DimensionBarProps = {
  className?: string;
  dimensionUnits?: DimensionUnits;
  disabled?: boolean;
  exactSizeAspectRatio?: number;
  isExactSizeValue?: (value: string) => boolean;
  /** `inside` draws each label inside its field box; the label stays a real `<label>`. */
  labelPlacement?: DimensionBarLabelPlacement;
  onError?: (hasError: boolean) => void;
  onLinkClick?: (isAspectRatioLocked: boolean) => void;
  setIsEditing: (isEditing: boolean) => void;
};

/**
 * @description This DimensionBar component is intended to be used in conjunction with a Formik form that contains an adjacent SingleSelect.
 * The form should include a `height` and a `width` field where each field is an integer/number. The Single Select should have at
 * least 2 values (Original and Custom).
 */
export const DimensionBar = ({
  dimensionUnits = 'px',
  disabled = false,
  exactSizeAspectRatio,
  isExactSizeValue,
  labelPlacement = 'outside',
  onError,
  onLinkClick,
  setIsEditing,
  className,
}: DimensionBarProps) => {
  const isLabelInside = labelPlacement === 'inside';
  const [
    { onChange: updateFormikWidth },
    { error: widthError, initialValue: initialWidth = 0, value: width },
    { setValue: setWidthValue },
  ] = useField<DimensionBarFormData['width']>('width');

  const [
    { onChange: updateFormikHeight },
    { error: heightError, initialValue: initialHeight = 0, value: height },
    { setValue: setHeightValue },
  ] = useField<DimensionBarFormData['height']>('height');

  const [{ value: formAspectRatio }, _a, { setValue: setFormAspectRatio }] =
    useField<DimensionBarFormData['size']>('size');

  const aspectRatio = Number(formAspectRatio);
  const isSelectValueCustom = formAspectRatio === SizeValue.Custom;
  const isSelectValueOriginal = formAspectRatio === SizeValue.Original;
  const isSelectValueExact = isExactSizeValue?.(formAspectRatio) ?? false;
  const isSelectValue = !(isSelectValueOriginal || isSelectValueCustom || isSelectValueExact);

  const [isWidthFocus, setIsWidthFocus] = useState(false);
  const [isHeightFocus, setIsHeightFocus] = useState(false);
  const [isAspectRatioLocked, setIsAspectRatioLocked] = useState(!!aspectRatio);
  const [hasRecalculatedBothFields, setHasRecalculatedBothFields] = useState(false);
  const [customAspectRatio, setCustomAspectRatio] = useState<number | null>();

  useEffect(() => {
    if (isSelectValueExact && exactSizeAspectRatio) {
      setCustomAspectRatio(exactSizeAspectRatio);
      setIsAspectRatioLocked(false);
    }
  }, [exactSizeAspectRatio, isSelectValueExact]);

  const originalAspectRatio = useMemo(
    () => calculateAspectRatio(initialWidth, initialHeight),
    [initialHeight, initialWidth],
  );

  const lockAspectRatio = () => setIsAspectRatioLocked(true);
  const unlockAspectRatio = () => setIsAspectRatioLocked(false);

  const updateCustomAspectRatio = useCallback(() => {
    const cAspectRatio = calculateAspectRatio(width, height);
    setCustomAspectRatio(cAspectRatio);
  }, [height, width]);

  // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
  const onClick = () => {
    isAspectRatioLocked ? unlockAspectRatio() : lockAspectRatio();
    const shouldSetCustomAspectRatio = formAspectRatio === SizeValue.Custom && !isAspectRatioLocked;

    // biome-ignore lint/style/useBlockStatements: PLA-1724 — pre-existing debt, not introduced by the icon migration
    if (shouldSetCustomAspectRatio) updateCustomAspectRatio();
    // biome-ignore lint/complexity/useOptionalChain: PLA-1724 — pre-existing debt, not introduced by the icon migration
    onLinkClick && onLinkClick(isAspectRatioLocked);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: PLA-1724 — pre-existing debt, not introduced by the icon migration
  const resetValues = useCallback(() => {
    setWidthValue(Number(initialWidth));
    setHeightValue(Number(initialHeight));
    // setHeightValue and setWidthValue are not stable
  }, [initialHeight, initialWidth]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: PLA-1724 — pre-existing debt, not introduced by the icon migration
  const updateWidthAndHeight = useCallback(() => {
    if (!hasRecalculatedBothFields) {
      setWidthValue(calculateDimensionWithAspectRatio(initialWidth || 1, aspectRatio));
      setHeightValue(calculateDimensionWithAspectRatio(initialHeight || 1, aspectRatio));
      setHasRecalculatedBothFields(true);
    } else {
      setHasRecalculatedBothFields(false);
    }
  }, [height, width, aspectRatio]);

  const handleInputChange = (hasHeightChanged: boolean, value: number) => {
    setFormAspectRatio(SizeValue.Custom);

    // biome-ignore lint/style/useBlockStatements: PLA-1724 — pre-existing debt, not introduced by the icon migration
    if (!isAspectRatioLocked) return;

    const aspectRatio = customAspectRatio ? customAspectRatio : originalAspectRatio;

    if (aspectRatio) {
      hasHeightChanged
        ? setWidthValue(calculateDimensionWithAspectRatio(value, 1 / aspectRatio))
        : setHeightValue(calculateDimensionWithAspectRatio(value, aspectRatio));
    }
  };

  // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name: inputName, value } = event.target;
    const hasHeightChanged = inputName === 'height';
    const latestValue = Number(value);

    // // NOTE: We need to call the onChange provided from Formik in order to keep formik updated
    // // see: https://stackoverflow.com/a/48980424/8560463
    hasHeightChanged ? updateFormikHeight(event) : updateFormikWidth(event);

    handleInputChange(hasHeightChanged, latestValue);
  };

  useEffect(() => {
    const allFieldsUnFocused = !isWidthFocus && !isHeightFocus;

    // biome-ignore lint/style/useBlockStatements: PLA-1724 — pre-existing debt, not introduced by the icon migration
    if (!allFieldsUnFocused) return;

    // biome-ignore lint/style/useBlockStatements: PLA-1724 — pre-existing debt, not introduced by the icon migration
    if (isSelectValue) updateWidthAndHeight();
    // biome-ignore lint/style/useBlockStatements: PLA-1724 — pre-existing debt, not introduced by the icon migration
    if (isSelectValueOriginal) resetValues();
  }, [isHeightFocus, isSelectValue, isSelectValueOriginal, isWidthFocus, resetValues, updateWidthAndHeight]);

  const hasError = !!heightError || !!widthError;

  const handleRowBlur = useCallback(() => setIsEditing(false), [setIsEditing]);
  const handleRowFocus = useCallback(() => setIsEditing(true), [setIsEditing]);

  useEffect(() => {
    if (onError) {
      onError(hasError);
    }
  }, [hasError, onError]);

  return (
    <div className={cn('flex max-h-[62px] flex-col justify-center ads:text-metadata text-gray-9', className)}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: focus and blur bubble from the width and height inputs. PLA-1724 — pre-existing debt, not introduced by the icon migration */}
      <div
        className={cn('flex items-center', isLabelInside ? 'gap-2' : 'gap-4')}
        onBlur={handleRowBlur}
        onFocus={handleRowFocus}
      >
        <DimensionBarInput
          data-testid={DIMENSION_BAR_WIDTH_INPUT}
          dimensionMeasurement="width"
          dimensionUnits={dimensionUnits}
          disabled={disabled}
          labelPlacement={labelPlacement}
          onChange={onChange}
          onFocus={setIsWidthFocus}
        />
        <Tooltip label={isAspectRatioLocked ? 'Unlock aspect ratio' : 'Lock aspect ratio'} side="top">
          <IconButton
            adsIcon
            // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
            data-testid={`${DIMENSION_BAR_ASPECT_RATIO_BUTTON}${isAspectRatioLocked ? 'LOCKED' : 'UNLOCKED'}`}
            disabled={disabled}
            icon={isAspectRatioLocked ? LockKeyholeIcon : LockKeyholeOpenIcon}
            onClick={onClick}
            appearance="ghost"
            color="gray"
            size={isLabelInside ? 'large' : 'small'}
            label={isAspectRatioLocked ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
          />
        </Tooltip>
        <DimensionBarInput
          data-testid={DIMENSION_BAR_HEIGHT_INPUT}
          dimensionMeasurement="height"
          dimensionUnits={dimensionUnits}
          labelPlacement={labelPlacement}
          onChange={onChange}
          disabled={disabled}
          onFocus={setIsHeightFocus}
        />
      </div>
      {hasError && (
        <p data-testid="DIMENSION_BAR_ERROR" className="mt-1 ads:text-error text-12 text-red-9">
          {widthError || heightError}
        </p>
      )}
    </div>
  );
};
